// File: apps/web/src/pages/signup/PlanSelectionPage.tsx
// Version: 2026.10.1 | Path: apps/web/src/pages/signup/PlanSelectionPage.tsx
// 2026.10.1: For a new user the main button says what it does: "Create account · <plan>", or "Create free account",
//            beside "Already have an account? Sign in" ("Continue with <plan>" did not read as creating one). The note under
//            it no longer names Stripe: billing may be on ForgeMint, whose checkout shows in ForgeTrack's own page.

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';

// ─── Design Tokens ───────────────────────────────────────────────────────────
const C = {
  bg:           '#050910',
  surface:      'rgba(8,16,32,0.88)',
  panel:        'rgba(12,22,44,0.92)',
  cardHover:    'rgba(16,28,52,0.95)',
  neon:         '#34d399',
  cyan:         '#22d3ee',
  violet:       '#a78bfa',
  amber:        '#fbbf24',
  coral:        '#f97066',
  blue:         '#60a5fa',
  text:         '#e8ecf2',
  textSoft:     'rgba(255,255,255,0.78)',
  muted:        'rgba(255,255,255,0.6)',
  dim:          'rgba(255,255,255,0.42)',
  faint:        'rgba(255,255,255,0.10)',
  neonGlow:     'rgba(52,211,153,0.5)',
  cyanGlow:     'rgba(34,211,238,0.45)',
  violetGlow:   'rgba(167,139,250,0.45)',
  amberGlow:    'rgba(251,191,36,0.45)',
};

const F = {
  display: "'Outfit', system-ui, -apple-system, sans-serif",
  mono:    "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace",
};

// ─── Keyframes ───────────────────────────────────────────────────────────────
const KEYFRAMES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  @keyframes neural-drift {
    0%   { transform: translate(0,0); }
    100% { transform: translate(40px,40px); }
  }
  @keyframes scan-sweep {
    0%   { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }
  @keyframes ping-ring {
    0%   { transform: scale(1); opacity: 0.8; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes glow-pulse {
    0%, 100% { opacity: 0.6; }
    50%       { opacity: 1; }
  }
  @keyframes float-up {
    0%   { transform: translateY(12px); opacity: 0; }
    100% { transform: translateY(0);    opacity: 1; }
  }
  @keyframes badge-pop {
    0%   { transform: scale(0.8) rotate(-2deg); opacity: 0; }
    60%  { transform: scale(1.1) rotate(1deg);  opacity: 1; }
    100% { transform: scale(1)   rotate(0deg);  opacity: 1; }
  }
  @keyframes price-reveal {
    0%   { opacity: 0; transform: translateY(6px) scale(0.95); }
    100% { opacity: 1; transform: translateY(0)   scale(1); }
  }

  .plan-card-selected {
    transition: box-shadow 0.3s ease, border-color 0.3s ease, transform 0.2s ease;
  }
  .shimmer-btn {
    background-size: 200% auto;
    animation: shimmer 2.5s linear infinite;
  }
`;

// ─── Types ───────────────────────────────────────────────────────────────────
type BillingCycle = 'monthly' | 'annual';
type PlanId = 'free' | 'builder' | 'professional' | 'enterprise';

interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  monthlyPrice: number;
  annualPrice: number;
  accent: string;
  glow: string;
  icon: string;
  badge?: string;
  projects: string;
  workItems: string;
  aiEstimations: string;
  exports: string;
  features: string[];
  stripePriceIdMonthly?: string;
  stripePriceIdAnnual?: string;
}

// ─── Plan Definitions ─────────────────────────────────────────────────────────
// NOTE: Replace STRIPE_PRICE_* placeholders with actual Stripe Price IDs from your dashboard.
const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For solo founders kicking the tires',
    monthlyPrice: 0,
    annualPrice: 0,
    accent: C.dim,
    glow: 'rgba(255,255,255,0.1)',
    icon: '⚙️',
    projects: '1 project',
    workItems: '50 work items',
    aiEstimations: '10 / month',
    exports: 'JSON, CSV',
    features: [
      'Community benchmarks',
      'Basic dashboard',
      'Work item tracking',
      'Session logging',
    ],
  },
  {
    id: 'builder',
    name: 'Builder',
    tagline: 'For growing teams building fast',
    monthlyPrice: 29,
    annualPrice: 290,
    accent: C.cyan,
    glow: C.cyanGlow,
    icon: '🔧',
    projects: '5 projects',
    workItems: 'Unlimited',
    aiEstimations: '100 / month',
    exports: '+ PDF, XLSX',
    features: [
      'All AI providers',
      'IRS report generator',
      'Engineering velocity dashboard',
      'CPA work paper export',
      'Priority email support',
    ],
    stripePriceIdMonthly: 'price_BUILDER_MONTHLY',
    stripePriceIdAnnual:  'price_BUILDER_ANNUAL',
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For serious R&D operations',
    monthlyPrice: 79,
    annualPrice: 790,
    accent: C.violet,
    glow: C.violetGlow,
    icon: '⚡',
    badge: 'MOST POPULAR',
    projects: 'Unlimited',
    workItems: 'Unlimited',
    aiEstimations: '500 / month',
    exports: '+ CPA, VC exports',
    features: [
      'Everything in Builder, plus:',
      'CPA Work Paper exports',
      'Investor Summary reports',
      'Investor Dashboard',
      'Git integration (GitHub, GitLab, Azure DevOps)',
      'Custom benchmarks',
      'API access',
    ],
    stripePriceIdMonthly: 'price_PRO_MONTHLY',
    stripePriceIdAnnual:  'price_PRO_ANNUAL',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'Audit-grade evidence. Multi-jurisdiction. Exam-ready.',
    monthlyPrice: -1,
    annualPrice: -1,
    accent: C.amber,
    glow: C.amberGlow,
    icon: '🏛️',
    projects: 'Unlimited',
    workItems: 'Unlimited',
    aiEstimations: 'Unlimited',
    exports: '+ white-label filing packages',
    features: [
      'Everything in Professional, plus:',
      'Adversarial self-review',
      'Multi-jurisdiction evidence (US, UK, CA, AU)',
      'Audit readiness scoring',
      'Multi-level reviewer workflows',
      'Statistical sampling (IRS-accepted)',
      'Evidence chain integrity (SHA-256)',
      'Engagement management',
      'Filing package generation',
      'Custom pipeline configuration',
      'Data residency controls',
      'SSO & custom domain',
      'Dedicated SLA & support',
    ],
  },
];

// ─── Hooks ───────────────────────────────────────────────────────────────────
function useCounter(target: number, duration: number, delay: number = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (target === 0) { setValue(0); return; }
      const start = Date.now();
      const tick = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, duration, delay]);
  return value;
}

// ─── Components ──────────────────────────────────────────────────────────────
function PulseDot({ color, size = 8 }: { color: string; size?: number }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        backgroundColor: color, opacity: 0.8,
      }} />
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        backgroundColor: color,
        animation: 'ping-ring 2s cubic-bezier(0,0,0.2,1) infinite',
      }} />
    </span>
  );
}

function CheckIcon({ color }: { color: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="7" cy="7" r="6.5" stroke={color} strokeOpacity="0.35" />
      <path d="M4 7l2 2 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PriceDisplay({ plan, cycle }: { plan: Plan; cycle: BillingCycle }) {
  const isCustom = plan.monthlyPrice < 0;
  const price = cycle === 'monthly' ? plan.monthlyPrice : Math.round(plan.annualPrice / 12);
  const count = useCounter(isCustom ? 0 : price, 400, 50);

  if (isCustom) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginBottom: 2 }}>
        <motion.span
          key={`${plan.id}-custom`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            fontFamily: F.mono,
            fontSize: 36,
            fontWeight: 700,
            color: plan.accent,
            lineHeight: 1,
            textShadow: `0 0 24px ${plan.glow}`,
          }}
        >
          Custom
        </motion.span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginBottom: 2 }}>
      <span style={{ fontFamily: F.display, fontSize: 13, color: C.muted, marginBottom: 10 }}>$</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={`${plan.id}-${cycle}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          style={{
            fontFamily: F.mono,
            fontSize: 48,
            fontWeight: 700,
            color: plan.monthlyPrice === 0 ? C.dim : plan.accent,
            lineHeight: 1,
            textShadow: plan.monthlyPrice === 0 ? 'none' : `0 0 24px ${plan.glow}`,
          }}
        >
          {plan.monthlyPrice === 0 ? '0' : count}
        </motion.span>
      </AnimatePresence>
      <span style={{ fontFamily: F.display, fontSize: 13, color: C.muted, marginBottom: 10 }}>/mo</span>
    </div>
  );
}

function PlanCard({
  plan,
  cycle,
  isSelected,
  onSelect,
  delay,
}: {
  plan: Plan;
  cycle: BillingCycle;
  isSelected: boolean;
  onSelect: () => void;
  delay: number;
}) {
  const [hovered, setHovered] = useState(false);
  const isFree = plan.monthlyPrice === 0;
  const isCustom = plan.monthlyPrice < 0;

  const borderColor = isSelected
    ? plan.accent
    : hovered
    ? `${plan.accent}66`
    : C.faint;

  const shadowStyle = isSelected
    ? `0 0 40px ${plan.glow}, 0 0 80px ${plan.glow}44, inset 0 0 40px ${plan.glow}11`
    : hovered
    ? `0 0 20px ${plan.glow}66`
    : 'none';

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: isSelected ? `rgba(12,22,44,0.97)` : C.surface,
        border: `1px solid ${borderColor}`,
        borderRadius: 16,
        padding: '28px 24px 24px',
        cursor: 'pointer',
        boxShadow: shadowStyle,
        transform: isSelected ? 'translateY(-4px) scale(1.01)' : hovered ? 'translateY(-2px)' : 'none',
        transition: 'all 0.25s cubic-bezier(0.16,1,0.3,1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* Scanline overlay */}
      {isSelected && (
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: `repeating-linear-gradient(0deg, transparent, transparent 2px, ${plan.glow}08 2px, ${plan.glow}08 4px)`,
          borderRadius: 16,
        }} />
      )}

      {/* Selected indicator top bar */}
      {isSelected && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, transparent, ${plan.accent}, transparent)`,
          borderRadius: '16px 16px 0 0',
          animation: 'glow-pulse 2s ease-in-out infinite',
        }} />
      )}

      {/* MOST POPULAR badge */}
      {plan.badge && (
        <div style={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          background: `linear-gradient(135deg, ${plan.accent}, ${plan.accent}cc)`,
          color: C.bg, fontFamily: F.mono, fontSize: 9, fontWeight: 700,
          letterSpacing: '0.12em', padding: '4px 12px', borderRadius: 100,
          whiteSpace: 'nowrap', boxShadow: `0 4px 16px ${plan.glow}`,
          animation: 'badge-pop 0.4s cubic-bezier(0.16,1,0.3,1) 0.6s both',
        }}>
          {plan.badge}
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 22 }}>{plan.icon}</span>
          <div>
            <div style={{
              fontFamily: F.display, fontSize: 18, fontWeight: 700,
              color: isSelected ? plan.accent : C.text,
              textShadow: isSelected ? `0 0 16px ${plan.glow}` : 'none',
            }}>
              {plan.name}
            </div>
          </div>
          {isSelected && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              style={{ marginLeft: 'auto' }}
            >
              <PulseDot color={plan.accent} size={8} />
            </motion.div>
          )}
        </div>

        {/* Price */}
        <PriceDisplay plan={plan} cycle={cycle} />

        {/* Annual savings hint */}
        <AnimatePresence>
          {cycle === 'annual' && !isFree && !isCustom && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                fontFamily: F.mono, fontSize: 10, color: C.neon,
                letterSpacing: '0.06em', marginBottom: 4,
              }}
            >
              ${plan.annualPrice}/yr · 2 MONTHS FREE
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tagline */}
        <p style={{
          fontFamily: F.display, fontSize: 12, color: C.muted,
          lineHeight: 1.5, marginBottom: 20,
        }}>
          {plan.tagline}
        </p>

        {/* Divider */}
        <div style={{
          height: 1,
          background: `linear-gradient(90deg, transparent, ${isSelected ? plan.accent : C.faint}, transparent)`,
          marginBottom: 16,
        }} />

        {/* Quotas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Projects',      value: plan.projects },
            { label: 'Work Items',    value: plan.workItems },
            { label: 'AI Estimates',  value: plan.aiEstimations },
            { label: 'Exports',       value: plan.exports },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: F.display, fontSize: 11, color: C.dim, letterSpacing: '0.04em' }}>
                {label}
              </span>
              <span style={{
                fontFamily: F.mono, fontSize: 11, fontWeight: 600,
                color: isSelected ? plan.accent : C.textSoft,
              }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: `linear-gradient(90deg, transparent, ${C.faint}, transparent)`,
          marginBottom: 14,
        }} />

        {/* Features */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {plan.features.map((feature) => (
            <div key={feature} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <CheckIcon color={isSelected ? plan.accent : C.muted} />
              <span style={{
                fontFamily: F.display, fontSize: 12, color: isSelected ? C.textSoft : C.muted,
                lineHeight: 1.4,
              }}>
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
//
// 2026.4.9: Refactored to a pure presentational component.
//
// Previously, PlanSelectionPage called POST /api/v1/billing/create-checkout-session
// directly, carrying tenant context (tenantId, tenantEmail, tenantSubdomain,
// successUrl, cancelUrl) in the request body. That endpoint has since been
// locked down as Owner-only with a trimmed { planId, billingCycle } DTO, and
// the new-tenant signup flow moved to POST /api/v1/signup/create-tenant which
// creates the tenant AND opens the Stripe session in one round trip.
//
// So: this component no longer makes ANY API calls. It picks a plan, hands
// the choice back to the parent via onContinue, and lets the parent orchestrate
// the appropriate API call for its flow. Two modes are supported:
//
//   mode="signup"  — Pre-auth new user. Parent is /signup; onContinue will
//                    kick off the MSAL redirect flow with the plan encoded
//                    in state. A "sign in instead" link is rendered for
//                    returning users.
//
//   mode="upgrade" — Authenticated existing tenant. Parent is /billing;
//                    onContinue will call useCreateCheckoutSession() and
//                    redirect to the returned Stripe URL. Free is not a
//                    valid target (the server rejects it — the button is
//                    disabled in this mode when Free is selected).
//
// The parent owns isSubmitting + error so a single spinner state covers the
// entire "click → API call → redirect" journey without double-spinners.

export type PlanSelectionPageMode = 'signup' | 'upgrade';

export interface PlanSelectionPageProps {
  /**
   * Controls button copy, whether "sign in instead" is shown, and whether
   * Free is selectable. See the comment block above for the full semantics.
   */
  mode: PlanSelectionPageMode;

  /**
   * Called when the user clicks the primary CTA. The parent handles all
   * API calls and navigation; this component does not fetch anything.
   */
  onContinue: (planId: PlanId, cycle: BillingCycle) => void;

  /**
   * Only used when mode === 'signup'. Called when the user clicks "I already
   * have an account". Omitted in upgrade mode — authenticated users clearly
   * already have an account.
   */
  onSignIn?: () => void;

  /**
   * Parent-controlled spinner state. True while the parent is calling
   * /signup/create-tenant or /billing/create-checkout-session.
   */
  isSubmitting?: boolean;

  /**
   * Parent-controlled error message. Rendered above the CTA in a coral
   * callout when set.
   */
  error?: string | null;

  /**
   * Upgrade mode only: the user's current plan. Used to highlight that
   * plan and prevent selecting it (you can't upgrade to the plan you're
   * already on). Ignored in signup mode.
   */
  currentPlanId?: PlanId;
}

export default function PlanSelectionPage({
  mode,
  onContinue,
  onSignIn,
  isSubmitting = false,
  error = null,
  currentPlanId,
}: PlanSelectionPageProps) {
  const [cycle, setCycle]             = useState<BillingCycle>('monthly');
  // Default selection: in upgrade mode, pick the next tier above the
  // current plan when possible; otherwise fall back to Professional
  // (the "Most Popular" choice in signup mode).
  const [selected, setSelected]       = useState<PlanId>(() => {
    if (mode === 'upgrade' && currentPlanId) {
      const order: PlanId[] = ['free', 'builder', 'professional', 'enterprise'];
      const idx = order.indexOf(currentPlanId);
      if (idx >= 0 && idx < order.length - 1) return order[idx + 1];
    }
    return 'professional';
  });
  const [utcTime, setUtcTime]         = useState('');
  const canvasRef                     = useRef<HTMLCanvasElement>(null);

  // UTC clock
  useEffect(() => {
    const tick = () => setUtcTime(new Date().toUTCString().slice(17, 25) + ' UTC');
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Neural grid canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let offset = 0;

    const draw = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const spacing = 40;
      ctx.strokeStyle = 'rgba(167,139,250,0.06)';
      ctx.lineWidth   = 1;

      for (let x = -spacing + (offset % spacing); x < canvas.width + spacing; x += spacing) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
      for (let y = -spacing + (offset % spacing); y < canvas.height + spacing; y += spacing) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
      }

      // Intersection dots
      ctx.fillStyle = 'rgba(167,139,250,0.12)';
      for (let x = -spacing + (offset % spacing); x < canvas.width + spacing; x += spacing) {
        for (let y = -spacing + (offset % spacing); y < canvas.height + spacing; y += spacing) {
          ctx.beginPath();
          ctx.arc(x, y, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      offset += 0.4;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  // ── CTA click handler ─────────────────────────────────────────────────────
  // Pure pass-through to the parent. No fetch, no navigation, no state
  // mutation beyond the already-captured (selected, cycle) pair. The parent
  // is responsible for every side effect that follows.
  const handleContinue = () => {
    const plan = PLANS.find(p => p.id === selected)!;

    // Enterprise / custom pricing → notify backend + open mailto as backup
    if (plan.monthlyPrice < 0) {
      // Fire-and-forget API call so admin gets notified even if mailto fails
      fetch('/api/v1/billing/enterprise-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: '', // will be filled by the signup flow or left empty
          companyName: '',
          message: 'Enterprise plan selected from pricing page',
        }),
      }).catch(() => {}); // non-blocking

      const subject = encodeURIComponent('ForgeTrack Enterprise Inquiry');
      const body = encodeURIComponent(
        `Hi ForgeTrack team,\n\nI'm interested in the Enterprise plan for my organization.\n\nPlease reach out to discuss custom pricing and capabilities.\n\nThank you`
      );
      window.open(`mailto:sales@forgetrack.io?subject=${subject}&body=${body}`, '_self');
      return;
    }

    // Upgrade mode guards: can't "upgrade" to Free (server rejects it with
    // 400) and can't re-pick the current plan. The button is also disabled
    // in these cases, but double-check here in case something odd happens.
    if (mode === 'upgrade') {
      if (selected === 'free') return;
      if (currentPlanId && selected === currentPlanId) return;
    }
    onContinue(selected, cycle);
  };

  const selectedPlan       = PLANS.find(p => p.id === selected)!;
  const isCurrentPlan      = mode === 'upgrade' && currentPlanId === selected;
  const isFreeInUpgradeMode = mode === 'upgrade' && selected === 'free';
  const ctaDisabled        = isSubmitting || isCurrentPlan || isFreeInUpgradeMode;

  return (
    <>
      <style>{KEYFRAMES}</style>

      <div style={{
        minHeight: '100vh',
        backgroundColor: C.bg,
        fontFamily: F.display,
        position: 'relative',
        overflowX: 'hidden',
      }}>
        {/* Neural grid */}
        <canvas ref={canvasRef} style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        }} />

        {/* Radial glow */}
        <div style={{
          position: 'fixed', top: -200, left: '50%', transform: 'translateX(-50%)',
          width: 900, height: 600, pointerEvents: 'none', zIndex: 0,
          background: `radial-gradient(ellipse at center, ${C.violetGlow}22 0%, transparent 70%)`,
        }} />

        {/* Scanline overlay */}
        <div style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(167,139,250,0.013) 3px, rgba(167,139,250,0.013) 4px)',
        }} />

        {/* Top HUD */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: 'sticky', top: 0, zIndex: 100,
            borderBottom: `1px solid ${C.faint}`,
            background: 'rgba(5,9,16,0.92)',
            backdropFilter: 'blur(20px)',
            padding: '12px 32px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: F.mono, fontSize: 11, color: C.violet, letterSpacing: '0.15em' }}>
              FORGETRACK
            </span>
            <span style={{ color: C.faint }}>·</span>
            <span style={{ fontFamily: F.mono, fontSize: 11, color: C.dim, letterSpacing: '0.1em' }}>
              SUBSCRIPTION SELECTION
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PulseDot color={C.neon} size={6} />
            <span style={{ fontFamily: F.mono, fontSize: 10, color: C.dim }}>{utcTime}</span>
          </div>
        </motion.div>

        {/* Main content */}
        <div style={{
          position: 'relative', zIndex: 1,
          maxWidth: 1160, margin: '0 auto', padding: '56px 24px 80px',
        }}>

          {/* Progress breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 48, justifyContent: 'center',
            }}
          >
            {['Account Created', 'Choose Plan', 'Confirm & Launch'].map((step, i) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  fontFamily: F.mono, fontSize: 10, letterSpacing: '0.1em',
                  color: i === 1 ? C.violet : i === 0 ? C.neon : C.dim,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {i === 0 && <span style={{ color: C.neon }}>✓</span>}
                  {i === 1 && <span style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: C.violet, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, color: C.bg, fontWeight: 700,
                  }}>2</span>}
                  {step}
                </div>
                {i < 2 && <span style={{ color: C.faint, fontSize: 14 }}>›</span>}
              </div>
            ))}
          </motion.div>

          {/* Hero header */}
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6, ease: [0.16,1,0.3,1] }}
              style={{
                fontFamily: F.display, fontSize: 40, fontWeight: 800,
                color: C.text, lineHeight: 1.15, marginBottom: 14,
                letterSpacing: '-0.02em',
              }}
            >
              Choose your{' '}
              <span style={{
                color: C.violet,
                textShadow: `0 0 40px ${C.violetGlow}`,
              }}>
                engine tier
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              style={{
                fontFamily: F.display, fontSize: 16, color: C.muted,
                maxWidth: 520, margin: '0 auto', lineHeight: 1.6,
              }}
            >
              Classify, document, and defend AI-augmented work — from solo
              builders to enterprise R&D operations.
            </motion.p>
          </div>

          {/* Billing cycle toggle */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              display: 'flex', justifyContent: 'center',
              alignItems: 'center', gap: 12, marginBottom: 48,
            }}
          >
            <span style={{
              fontFamily: F.mono, fontSize: 11, letterSpacing: '0.08em',
              color: cycle === 'monthly' ? C.text : C.dim,
            }}>
              MONTHLY
            </span>

            <button
              onClick={() => setCycle(c => c === 'monthly' ? 'annual' : 'monthly')}
              style={{
                position: 'relative', width: 52, height: 28,
                borderRadius: 100, border: `1px solid ${C.faint}`,
                background: 'rgba(12,22,44,0.8)', cursor: 'pointer',
                padding: 0, outline: 'none',
              }}
            >
              <motion.div
                animate={{ x: cycle === 'annual' ? 26 : 2 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={{
                  position: 'absolute', top: 3, left: 0,
                  width: 20, height: 20, borderRadius: '50%',
                  background: cycle === 'annual'
                    ? `linear-gradient(135deg, ${C.neon}, ${C.cyan})`
                    : `linear-gradient(135deg, ${C.violet}, ${C.cyan})`,
                  boxShadow: `0 0 12px ${cycle === 'annual' ? C.neonGlow : C.violetGlow}`,
                }}
              />
            </button>

            <span style={{
              fontFamily: F.mono, fontSize: 11, letterSpacing: '0.08em',
              color: cycle === 'annual' ? C.text : C.dim,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              ANNUAL
              <AnimatePresence>
                {cycle === 'annual' && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8, x: -4 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    style={{
                      background: `linear-gradient(135deg, ${C.neon}33, ${C.cyan}33)`,
                      border: `1px solid ${C.neon}55`,
                      color: C.neon, padding: '2px 8px', borderRadius: 100,
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                    }}
                  >
                    2 MONTHS FREE
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </motion.div>

          {/* Plan cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            marginBottom: 40,
            alignItems: 'start',
          }}>
            {PLANS.map((plan, i) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                cycle={cycle}
                isSelected={selected === plan.id}
                onSelect={() => setSelected(plan.id)}
                delay={0.35 + i * 0.07}
              />
            ))}
          </div>

          {/* Overage note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            style={{
              textAlign: 'center', fontFamily: F.mono, fontSize: 10,
              color: C.dim, letterSpacing: '0.06em', marginBottom: 48,
            }}
          >
            AI estimation overage: $0.01 per call beyond tier limit · Cancel anytime · No contracts
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 16 }}
          >
            {error && (
              <div style={{
                background: 'rgba(249,112,102,0.1)', border: `1px solid ${C.coral}44`,
                borderRadius: 10, padding: '10px 20px',
                fontFamily: F.mono, fontSize: 11, color: C.coral,
                letterSpacing: '0.04em',
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handleContinue}
              disabled={ctaDisabled}
              style={{
                fontFamily: F.display, fontSize: 15, fontWeight: 700,
                color: C.bg,
                background: ctaDisabled
                  ? 'rgba(12,22,44,0.8)'
                  : selectedPlan.monthlyPrice === 0
                    ? `linear-gradient(135deg, ${C.neon}, ${C.cyan})`
                    : `linear-gradient(135deg, ${selectedPlan.accent}, ${selectedPlan.accent}cc)`,
                border: 'none', borderRadius: 12, cursor: ctaDisabled ? 'not-allowed' : 'pointer',
                padding: '16px 56px', letterSpacing: '0.02em',
                boxShadow: ctaDisabled ? 'none' : `0 8px 32px ${selectedPlan.glow}`,
                transition: 'all 0.2s ease',
                position: 'relative', overflow: 'hidden',
                minWidth: 280, opacity: ctaDisabled ? 0.55 : 1,
              }}
            >
              {isSubmitting ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                  <span style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: `2px solid ${C.muted}`,
                    borderTopColor: C.text,
                    display: 'inline-block',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  {mode === 'signup' ? 'Preparing your workspace…' : 'Connecting to Stripe…'}
                </span>
              ) : isCurrentPlan ? (
                `✓ Current plan`
              ) : isFreeInUpgradeMode ? (
                `Free is not an upgrade target`
              ) : selectedPlan.monthlyPrice < 0 ? (
                '→ Contact Us'
              ) : mode === 'upgrade' ? (
                `→ Upgrade to ${selectedPlan.name}`
              ) : selectedPlan.monthlyPrice === 0 ? (
                '→ Create free account'
              ) : (
                `→ Create account · ${selectedPlan.name}`
              )}
            </button>

            <p style={{
              fontFamily: F.display, fontSize: 12, color: C.dim, textAlign: 'center',
            }}>
              {selectedPlan.monthlyPrice < 0
                ? 'Our team will build a custom package for your organization'
                : mode === 'signup' && selectedPlan.monthlyPrice === 0
                ? 'No credit card required'
                : mode === 'signup'
                  ? `You'll create your account, then pay on our secure checkout`
                  : isFreeInUpgradeMode
                    ? 'To downgrade, contact support'
                    : isCurrentPlan
                      ? 'Select a different plan to change your subscription'
                      : `You'll pay on our secure checkout`}
            </p>

            {mode === 'signup' && onSignIn && (
              <button
                type="button"
                onClick={onSignIn}
                disabled={isSubmitting}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: C.muted,
                  fontFamily: F.mono,
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  padding: '8px 12px',
                  marginTop: 4,
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => { if (!isSubmitting) (e.target as HTMLButtonElement).style.color = C.text; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.color = C.muted; }}
              >
                Already have an account? Sign in →
              </button>
            )}
          </motion.div>

          {/* Security badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              gap: 28, marginTop: 56,
            }}
          >
            {[
              { icon: '🔒', label: 'TLS Encrypted' },
              { icon: '🏦', label: 'Stripe Payments' },
              { icon: '📄', label: 'SOC 2 Compliant' },
              { icon: '🔁', label: 'Cancel Anytime' },
            ].map(({ icon, label }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: F.display, fontSize: 11, color: C.dim,
              }}>
                <span>{icon}</span>
                <span>{label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Bottom HUD */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          borderTop: `1px solid ${C.faint}`,
          background: 'rgba(5,9,16,0.92)', backdropFilter: 'blur(20px)',
          padding: '8px 32px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          zIndex: 100,
        }}>
          <span style={{ fontFamily: F.mono, fontSize: 9, color: C.dim, letterSpacing: '0.12em' }}>
            ForgeTrack · Edson Technologies
          </span>
          <span style={{ fontFamily: F.mono, fontSize: 9, color: C.dim, letterSpacing: '0.1em' }}>
            Powered by skipLogic™
          </span>
        </div>
      </div>
    </>
  );
}
